'use client'

import React, { Component, useState, type ReactNode, type ErrorInfo } from 'react'
import { AlertTriangle, RefreshCw, MessageSquare } from 'lucide-react'
import { Button } from './Button'
import { useFeedbackStore } from '../stores/feedbackStore'
import { capturePageScreenshot } from '../lib/errorReport/captureScreenshot'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

// Wrapper component to access the Zustand store
function ErrorFallback({
  error,
  errorInfo,
  onReset,
}: {
  error: Error
  errorInfo: ErrorInfo | null
  onReset: () => void
}) {
  const { openErrorReport } = useFeedbackStore()
  const [capturing, setCapturing] = useState(false)

  const handleReportError = async () => {
    if (capturing) return
    setCapturing(true)
    try {
      // The crashed subtree is gone, so this captures the fallback screen —
      // still useful for showing which page broke. The stack matters more here.
      const screenshot = await capturePageScreenshot()
      openErrorReport({
        report: {
          message: error.message || 'Unknown render error',
          stack: error.stack,
          componentStack: errorInfo?.componentStack || undefined,
          source: 'render',
        },
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        timestamp: new Date().toISOString(),
        screenshot,
      })
    } finally {
      setCapturing(false)
    }
  }

  const handleRefresh = () => {
    onReset()
    window.location.reload()
  }

  return (
    <div className="min-h-[400px] flex items-center justify-center p-8">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="w-8 h-8 text-red-600" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Oops! Something went wrong
        </h2>
        <p className="text-gray-600 mb-6">
          We encountered an unexpected error. You can try refreshing the page, or report this issue to help us fix it.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button variant="secondary" onClick={handleRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh Page
          </Button>
          <Button onClick={handleReportError} disabled={capturing}>
            <MessageSquare className="w-4 h-4 mr-2" />
            {capturing ? 'One moment…' : 'Report Issue'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo })

    // Log to console for debugging
    console.error('[ErrorBoundary] Caught error:', error)
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack)
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <ErrorFallback
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      )
    }

    return this.props.children
  }
}
